import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormControl, FormGroup } from '@angular/forms';
import { I18nCallerCorrectionEnum, I18nKeys } from '../shared/models/i18n.model';
import {
	CallerCorrectionRequestDTO,
	CallerEditDialogParams,
	SyncToAddressBookEnum,
	TelephoneTypeEnum,
} from '../shared/models/caller-correction.model';
import { LocalizationService } from '../shared/services/localization.service';

@Component({
	selector: 'app-caller-edit-dialog',
	templateUrl: './caller-edit-dialog.html',
	styleUrl: './caller-edit-dialog.scss',
	standalone: false,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CallerEditDialog implements OnInit {
	public params: CallerEditDialogParams = inject<CallerEditDialogParams>(MAT_DIALOG_DATA);
	protected readonly I18N = I18nKeys;
	protected readonly I18N_CC = I18nCallerCorrectionEnum;
	protected readonly SyncEnum = SyncToAddressBookEnum;
	protected readonly TelTypeEnum = TelephoneTypeEnum;

	protected readonly localizationService = inject(LocalizationService);
	private readonly dialogRef = inject(MatDialogRef);
	private readonly destroyRef = inject(DestroyRef);

	// Whether the caller is linked to an addressbook entry (drives dialog mode)
	public isLinked = false;

	// Salutation options
	public salutationOptions = ['', 'Herr', 'Frau'];

	// Phone type options
	public phoneTypeOptions = [
		{ value: TelephoneTypeEnum.PHONE, label: 'Telefon' },
		{ value: TelephoneTypeEnum.MOBILE, label: 'Mobil' },
		{ value: TelephoneTypeEnum.FAX, label: 'Fax' },
	];

	public form = new FormGroup({
		salutation: new FormControl<string>('', { nonNullable: true }),
		firstName: new FormControl<string>('', { nonNullable: true }),
		lastName: new FormControl<string>('', { nonNullable: true }),
		company: new FormControl<string>('', { nonNullable: true }),
		telephoneType: new FormControl<string>(TelephoneTypeEnum.PHONE, { nonNullable: true }),
		telephoneNumber: new FormControl<string>('', { nonNullable: true }),
		syncToAddressBook: new FormControl<SyncToAddressBookEnum>(SyncToAddressBookEnum.NO, {
			nonNullable: true,
		}),
	});

	// Reactive signals for validation state
	public syncValue = signal<SyncToAddressBookEnum>(SyncToAddressBookEnum.NO);
	public isCreateMode = computed(() => this.syncValue() === SyncToAddressBookEnum.CREATE);
	public createValidationError = signal(false);

	ngOnInit(): void {
		const caller = this.params.caller;
		this.isLinked = this.params.syncedWithAddressBook;

		// Pre-fill form
		this.form.patchValue({
			salutation: caller.salutation ?? '',
			firstName: caller.firstname ?? '',
			lastName: caller.lastname ?? '',
			company: caller.company ?? '',
			telephoneNumber: caller.telephoneNumbers?.[0]?.number ?? '',
		});

		// Default sync mode based on linked status
		const defaultSync = this.isLinked ? SyncToAddressBookEnum.UPDATE : SyncToAddressBookEnum.NO;
		this.form.get('syncToAddressBook')?.setValue(defaultSync);
		this.syncValue.set(defaultSync);

		// Track sync value changes
		this.form.get('syncToAddressBook')?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((val) => {
			this.syncValue.set(val);
			this.createValidationError.set(false);
		});
	}

	public onSave(): void {
		const formVal = this.form.getRawValue();

		// CREATE/UPDATE validation: firstName OR lastName OR company required
		const requiresIdentity =
			formVal.syncToAddressBook === SyncToAddressBookEnum.CREATE ||
			formVal.syncToAddressBook === SyncToAddressBookEnum.UPDATE;
		if (requiresIdentity) {
			const hasAnyName = formVal.firstName.trim() || formVal.lastName.trim();
			const hasCompany = !!formVal.company.trim();
			if (!hasAnyName && !hasCompany) {
				this.createValidationError.set(true);
				return;
			}
		}

		const request: CallerCorrectionRequestDTO = {
			salutation: formVal.salutation || null,
			firstName: formVal.firstName || null,
			lastName: formVal.lastName || null,
			company: formVal.company || null,
			formfields: formVal.telephoneNumber
				? [{ type: formVal.telephoneType, name: formVal.telephoneNumber }]
				: [],
			syncToAddressBook: formVal.syncToAddressBook,
		};

		this.dialogRef.close({ uid: this.params.uid, request });
	}
}
